/* eslint-disable max-lines */
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { ITripType } from "../../../interfaces/endpoints/trips/Trips.types";
import { getTripsByLine } from "../../../services/Trips.service";
import {
  MAX_TRIP_DURATION_MINUTES,
  calendarDaysAfter,
  minutesToTimeString,
  timeStringToMinutes
} from "../../../utils/tripDuration";
import Input from "../../ui/input/Input";
import { SelectOption } from "../../ui/input/Input.types";
import ReadOnlyClockField from "../../ui/input/ReadOnlyClockField";
import SelectInput from "../../ui/input/SelectInput";
import TimePartsField, {
  HOUR_OPTIONS_DURATION,
  pad2
} from "../../ui/input/TimePartsField";
import useTimeList from "../../ui/input/TimePicker";
import CreateTripWrapper from "./schedules/CreateTripWrapper";

interface NewTripProps {
  response: any;
  editMode: boolean;
}

const NewTrip: FC<NewTripProps> = ({ response, editMode }) => {
  const {
    register,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors }
  } = useFormContext<ITripType>();

  const [searchParams] = useSearchParams();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const currencyRef = useRef<HTMLInputElement>(null);
  const departureRef = useRef<HTMLInputElement>(null);
  // One entry per minute of the day, 00:00 to 23:59: the single scrolling list the
  // operators are used to for the departure. The length keeps its own hours:minutes box.
  const times = useTimeList();
  // The last value the line-prefill wrote, so switching lines can tell "still
  // my own suggestion" (safe to replace) from "the operator typed this" (keep).
  const lastPrefillRef = useRef<number | null>(null);
  // Monotonic request counter: only the LATEST prefill request may write, so a
  // slow response for a previously-picked line cannot overwrite the newer one.
  const prefillSeqRef = useRef(0);

  const departureTimeString = watch("trip.departureTimeString");
  const durationMinutes = watch("trip.durationMinutes");

  // The length box reads and writes the same form field the rest of the form (payload,
  // schema, preview) already uses: total minutes.
  // The picked hour/minute parts, kept so "00:00" stays visible with its validation
  // message instead of collapsing back to the empty placeholder (a 0 total reads as
  // "no value" everywhere else) or being silently bumped to 00:01.
  const [durationDraft, setDurationDraft] = useState<{ hours: string; minutes: string } | null>(null);
  const hasDuration =
    typeof durationMinutes === "number" && !isNaN(durationMinutes) && durationMinutes >= 1;
  const durationHour = hasDuration ? pad2(Math.floor(durationMinutes / 60)) : durationDraft?.hours ?? "";
  const durationMinute = hasDuration ? pad2(durationMinutes % 60) : durationDraft?.minutes ?? "";

  const setDurationPart = (part: "hours" | "minutes", value: string) => {
    const hours = part === "hours" ? value : durationHour || "00";
    const minutes = part === "minutes" ? value : durationMinute || "00";
    // No clamp to 1: a 00:00 pick is kept as 0 so the schema's "at least 1 minute"
    // error shows under the box and the save is blocked, instead of a silent 00:01.
    const total = Math.min(Number(hours) * 60 + Number(minutes), MAX_TRIP_DURATION_MINUTES);
    setDurationDraft({ hours, minutes });
    lastPrefillRef.current = null;
    setValue("trip.durationMinutes", total, { shouldValidate: true });
    // Each stop's distance is checked against the length, so the stops are re-checked with it.
    const stops = getValues("schedules") ?? [];
    const stopLengthFields = stops
      .map((stop, i) => (stop ? (`schedules.${i}.duration` as const) : null))
      .filter(Boolean) as `schedules.${number}.duration`[];
    void trigger(stopLengthFields);
  };

  // When a line is picked for a NEW trip, prefill the length with the line's
  // most common existing one (same route, so usually the same length) - the
  // operator then only sets the departure. Never overwrites a typed value.
  const prefillDurationFromLine = useCallback(
    async (lineId: number) => {
      if (editMode || !lineId) return;
      const untouched = () => {
        const current = getValues("trip.durationMinutes");
        if (!(typeof current === "number" && current >= 1)) return true;
        return current === lastPrefillRef.current;
      };
      if (!untouched()) return;
      const seq = ++prefillSeqRef.current;
      try {
        const res = await getTripsByLine(lineId);
        if (seq !== prefillSeqRef.current) return;
        // Two tallies, deliberately not one. A DECLARED length is the operator's own statement
        // of how long the trip takes and can be any value up to four days. A length derived
        // from the clock pair cannot exceed 23h59 by construction - it is a wrapped time of
        // day, not a length. On a line that really runs 28h, a legacy row that has not been
        // given a declared length yet derives to 4h, and mixing the two tallies let that 4h
        // outvote the truth and prefill a new trip on that line with a length off by a day.
        // So the derived values are only consulted when NOTHING on the line declares a length,
        // which is the ordinary sub-24h line the prefill was built for.
        const declaredCounts = new Map<number, number>();
        const derivedCounts = new Map<number, number>();
        (res?.data ?? []).forEach((row) => {
          const trip = row?.trip;
          if (!trip) return;
          const declared =
            typeof trip.durationMinutes === "number" && trip.durationMinutes > 0
              ? trip.durationMinutes
              : NaN;
          const tally = (map: Map<number, number>, length: number) => {
            if (!isNaN(length) && length >= 1 && length <= MAX_TRIP_DURATION_MINUTES) {
              map.set(length, (map.get(length) ?? 0) + 1);
            }
          };
          if (!isNaN(declared)) {
            tally(declaredCounts, declared);
            return;
          }
          let derived =
            timeStringToMinutes(trip.arrivalTimeString) -
            timeStringToMinutes(trip.departureTimeString);
          if (derived < 0) derived += 1440;
          tally(derivedCounts, derived);
        });
        const counts = declaredCounts.size > 0 ? declaredCounts : derivedCounts;
        let best: number | null = null;
        counts.forEach((count, length) => {
          if (best === null || count > (counts.get(best) ?? 0)) best = length;
        });
        if (!untouched()) return;
        if (best !== null) {
          lastPrefillRef.current = best;
          setValue("trip.durationMinutes", best, { shouldValidate: true });
        } else if (lastPrefillRef.current !== null) {
          // The new line has nothing to suggest: the previous line's suggestion must not pass
          // for its own.
          lastPrefillRef.current = null;
          setDurationDraft(null);
          setValue("trip.durationMinutes", NaN);
        }
      } catch {
        // Prefill is a convenience; a failed lookup just leaves the field empty.
      }
    },
    [editMode, getValues, setValue]
  );

  useEffect(() => {
    if (searchParams.has("lineId") && !editMode) {
      const lineIdFromParams = searchParams.get("lineId");
      if (lineIdFromParams && !getValues("trip.lineId")) {
        setValue("trip.lineId", Number(lineIdFromParams), { shouldValidate: true });
        void prefillDurationFromLine(Number(lineIdFromParams));
      }
    }
  }, [searchParams, setValue, getValues, editMode, response, prefillDurationFromLine]);

  useEffect(() => {
    if (!getValues("trip.defaultCurrencyId")) {
      setValue("trip.defaultCurrencyId", 1, { shouldValidate: true });
    }
  }, [setValue, getValues]);

  // LEGACY (removed 2026-09-16):
  //   const updateDuration = () => {
  //     const ft = dayjs(`2000-01-01 ${getValues("trip.departureTimeString")}`);
  //     const tt = dayjs(`2000-01-01 ${getValues("trip.arrivalTimeString")}`);
  //     const mins: number = Number(tt.diff(ft, "minutes", true));
  //     setValue("trip.duration", mins);
  //   };
  // Keep the legacy arrival fields consistent for older backend code: the
  // arrival is always (departure + length) on a 24h clock.
  useEffect(() => {
    if (
      departureTimeString &&
      typeof durationMinutes === "number" &&
      durationMinutes >= 1
    ) {
      const arrival = minutesToTimeString(
        timeStringToMinutes(departureTimeString) + durationMinutes
      );
      if (getValues("trip.arrivalTimeString") !== arrival) {
        setValue("trip.arrivalTimeString", arrival, { shouldValidate: true });
      }
    }
  }, [departureTimeString, durationMinutes, getValues, setValue]);

  // The arrival is worked out, never typed: departure plus length, with the day it lands on.
  const hasTiming =
    !!departureTimeString &&
    !isNaN(timeStringToMinutes(departureTimeString)) &&
    typeof durationMinutes === "number" &&
    durationMinutes >= 1;
  const arrivalClock = hasTiming
    ? minutesToTimeString(timeStringToMinutes(departureTimeString) + durationMinutes)
    : "";
  const arrivalDays = hasTiming ? calendarDaysAfter(departureTimeString, durationMinutes) : 0;

  const toggleDropdown = (dropdown: string) => {
    setOpenDropdown((current) => current === dropdown ? null : dropdown);
  };

  const handleOptionSelect = (formField: keyof ITripType | string, option: SelectOption | null) => {
    setOpenDropdown(null);
    if (!option) {
      // Cleared with Backspace or Delete: a clock field empties, a picked id stays.
      if (formField.includes("TimeString")) setValue(formField as any, "", { shouldValidate: true });
      return;
    }
    const value = formField.includes("TimeString") ? option.id : Number(option.id);
    setValue(formField as any, value, { shouldValidate: true });
  };

  return (
    <CreateTripWrapper title="Orari">
      <div className="absolute right-5 top-0 flex items-center flex-col">
        <div className="flex items-center transitions hover:scale-[1.01] cursor-pointer transition-all
        duration-75 select-none">
          <input
            type="checkbox"
            id="reserve"
            className="cursor-pointer text-primary bg-primary"
            {...register("trip.isReservable")}
          />
          <label
            htmlFor="reserve"
            className="ml-3 text-sm cursor-pointer text-primary font-semibold"
          >
            Bëje të rezervueshme në GjirafaTravel
          </label>
        </div>
        {typeof errors.trip?.isReservable?.message === "string" && (
          <div className="text-red-500 text-xs mt-0.5">
            {errors.trip?.isReservable?.message}
          </div>
        )}
      </div>
      <div className="md:flex-row flex-col md:flex w-full">
        <div className="mb-6 w-full md:w-1/2 md:pr-4">
          {!editMode ? (
            <SelectInput
              label="Zgjedh linjën"
              placeholder="Zgjedh"
              options={response?.data?.result?.map((el: any) => {
                return { name: el.label, id: el.value };
              }) || []}
              selectedId={
                getValues("trip.lineId") ||
                (searchParams.has("lineId") ? Number(searchParams.get("lineId")) : undefined)
              }
              isOpen={openDropdown === "line"}
              onClick={() => toggleDropdown("line")}
              onOptionSelect={(option) => {
                handleOptionSelect("trip.lineId", option);
                void prefillDurationFromLine(Number(option.id));
              }}
              error={errors.trip?.lineId?.message}
            />
          ) : (
            <Input
              {...register("routeName")}
              label="Linja e zgjedhur"
              className="pointer-events-none"
              inputClassName="h-11"
            />
          )}
        </div>
        <div className="w-full md:w-1/2 flex">
          {/* LEGACY (removed 2026-09-16):
  const times = useTimeList();

          <div className="mb-6 w-2/5 pr-4">
            <SelectInput
              inputRef={arrivalRef}
              label="Ora e arritjes"
              options={times}
              selectedId={getValues("trip.arrivalTimeString")}
              isOpen={openDropdown === "arrival"}
              onClick={() => toggleDropdown("arrival")}
              onOptionSelect={(option) => {
                handleOptionSelect("trip.arrivalTimeString", option);
                updateDuration();
              }}
              onEnter={() => arrivalRef.current?.blur()}
              error={errors.trip?.arrivalTimeString?.message}
            />
          </div>
          */}
          <div className="mb-6 w-[30%] pr-4">
            <SelectInput
              inputRef={departureRef}
              label="Ora e nisjes"
              placeholder="Zgjedh"
              options={times}
              selectedId={(departureTimeString || "").slice(0, 5)}
              isOpen={openDropdown === "departure"}
              onClick={() => toggleDropdown("departure")}
              onOptionSelect={(option) => handleOptionSelect("trip.departureTimeString", option)}
              onEnter={() => departureRef.current?.blur()}
              error={errors.trip?.departureTimeString?.message}
            />
          </div>
          <div className="mb-6 w-[30%] pr-4">
            <TimePartsField
              id="tripDurationMinutes"
              label="Kohëzgjatja"
              hourOptions={HOUR_OPTIONS_DURATION}
              hours={durationHour}
              minutes={durationMinute}
              onPartChange={setDurationPart}
              error={errors.trip?.durationMinutes?.message}
            />
          </div>
          <div className="mb-6 w-[22%] pr-4">
            <ReadOnlyClockField
              id="tripArrivalPreview"
              label="Ora e arritjes"
              clock={arrivalClock}
              days={arrivalDays}
            />
          </div>
          <div className="mb-6 w-[18%]">
            <SelectInput
              inputRef={currencyRef}
              label="Valuta"
              placeholder="Zgjedh"
              options={[
                { name: "Euro", id: 1 },
                { name: "Lekë", id: 2 }
              ]}
              selectedId={getValues("trip.defaultCurrencyId") || 1}
              isOpen={openDropdown === "currency"}
              onClick={() => toggleDropdown("currency")}
              onOptionSelect={(option) => handleOptionSelect("trip.defaultCurrencyId", option)}
              error={errors.trip?.defaultCurrencyId?.message}
              className="w-full"
            />
          </div>
        </div>
      </div>
    </CreateTripWrapper>
  );
};

export default NewTrip;
